/** Shared raid battle types (Phaser-free). */

import type { BehaviorState, HitSignal, OrderSlot, AggroTable } from "../../sim";
import type { RaidClassId } from "../../data/raid-catalog";
import type { RaidSimEvent } from "./raid-events";

/** Commander short-directive kinds (order-channel payloads). */
export type RaidOrderKind = "focusTarget" | "regroup" | "retreat" | "hold";

/** Per-unit gesture order kinds (swipe dash / hold pin). */
export type RaidUnitOrderKind = "dash" | "pin";

/** Gesture formation hold — TTL after dash/groupDash/pin; order clear does not clear this. */
export type RaidFormationAnchor = {
  x: number;
  y: number;
  /** Absolute sim time; inactive when `time >= until`. */
  until: number;
};

/** Per-unit decision memory (engine behavior/hysteresis state + sticky target). */
export type RaidUnitBrain = {
  select: BehaviorState;
  /** Sticky engage target: add unit id, or -1 = none/boss. */
  targetId: number;
  /** Staggered re-evaluation time (decide runs at ~5Hz, steer every tick). */
  nextDecideAt: number;
  /** Active unit-level gesture order (dash/pin); outranks commander tactics. */
  order: OrderSlot<RaidUnitOrderKind>;
  /**
   * Soft formation reference after dash/pin (Approach A).
   * Survives order clear until TTL; engage/space prefer this over ally centroid.
   */
  gestureAnchor: RaidFormationAnchor | null;
  /**
   * Absolute sim time when DangerZone reaction (T0 dodge) may begin.
   * 0 = not in a danger episode. Instinct sets delay from first entry.
   */
  dangerReactAt: number;
  /**
   * Hostile unit ids last seen inside weapon reach (engage range gate).
   * Rising edge vs this list forces open retarget at high bond.
   */
  engageRangeIds: number[];
  /**
   * SoftThreat space hysteresis — stays true until feet clear
   * max(Soft melee disk, enemy hit reach) by softSpaceExitPad.
   */
  softSpaceLatch: boolean;
  /** Latest thrall pack-exit seat XY (telemetry / debug). */
  thrallEscapeSeat: { x: number; y: number } | null;
  /** Prior-tick thrall-in-AA count — rise lengthens pack re-clear step. */
  thrallEscapeAa: number;
};

/** playing → bossDying → won | playing → losing → lost */
export type RaidPhase = "playing" | "bossDying" | "losing" | "won" | "lost";

export type RaidDanger = "yellow" | "red";
export type RaidBossSkillId = "slam" | "brass" | "leap" | "charge";

/** Enemy-only situation ticker slots — clear by key, not fire-and-forget.
 * Commander orders/gestures must NOT write here (use `commander.lastActionKey` + HUD prep chip). */
export const RaidSituation = {
  BossBreak: "boss_break",
  BossCastBrass: "boss_cast_brass",
} as const;
export type RaidSituationId =
  (typeof RaidSituation)[keyof typeof RaidSituation];

export type RaidSituationSlot = {
  messageKey: string;
  /** Absolute sim time; omit = sticky until explicit clear / condition false. */
  until?: number;
};

export type RaidTelegraph = {
  shape: "circle" | "cone" | "line";
  range: number;
  coneDeg: number;
  aimAngle: number;
  remain: number;
  duration: number;
  windupSec: number;
  damage: number;
  danger: RaidDanger;
  skillId: RaidBossSkillId;
  /** Line corridor half-width (world px). Used when shape==="line". */
  halfWidth?: number;
  /** Line start offset from body toward aim (head/crest). */
  originPad?: number;
};

export type RaidLeap = {
  phase: "windup" | "travel";
  remain: number;
  duration: number;
  landX: number;
  landY: number;
  fromX: number;
  fromY: number;
  landRadius: number;
  landDamage: number;
  danger: RaidDanger;
  /**
   * Combat backline leap vs edge-camp recenter leap (same red land pipeline).
   * `charge` = ironhorn (or charger-style) straight-line dash — corridor hits, not land AoE.
   */
  purpose: "combat" | "recenter" | "charge";
};

export type RaidBoss = {
  x: number;
  y: number;
  radius: number;
  barsTotal: number;
  /** 0 = first (top) bar currently active. */
  barIndex: number;
  barHp: number;
  barMaxHp: number;
  phase: 1 | 2;
  phase2Triggered: boolean;
  /** Slam AoE skill cooldown. */
  atkCd: number;
  brassCd: number;
  leapCd: number;
  /** Seconds until next periodicAdd twin pulse (table `periodicAdd.cdSec`). */
  periodicAddCdLeft: number;
  /** One-shot lateSummon wave gates (index matches `lateSummon.waves`). */
  lateSummonWavesFired: boolean[];
  /** One-shot boss-fight charger pack (`bosses.chargerSpawn`) when barsRemaining gate hits. */
  chargerSpawnFired: boolean;
  /** Seconds until next occasional boss bark is allowed. */
  barkCdLeft: number;
  /** Melee basic-attack cooldown. */
  meleeCd: number;
  stunnedUntil: number;
  /** Duration of current break stun (for remain-frac HUD); 0 when not broken. */
  breakStunDuration: number;
  toughness: number;
  toughnessMax: number;
  telegraph: RaidTelegraph | null;
  leap: RaidLeap | null;
  damage: number;
  attackRange: number;
  speed: number;
  /** Stop chasing when within attackRange - chaseStopPad of target. */
  chaseStopPad: number;
  vx: number;
  vy: number;
  intentVx: number;
  intentVy: number;
  /** Melee AA swing timer only (seconds remaining). Skills use castAnim, not this. */
  attackAnimT: number;
  /** True until melee hit frame resolves within the current swing. */
  meleeHitArmed: boolean;
  /** Accumulated seconds near playBounds edge / far from center (table edgeRecenter). */
  edgeDwellSec: number;
  /** Cooldown after an edge-recenter leap/steer (seconds remaining). */
  edgeRecenterCdLeft: number;
  /** Soft-steer window toward arena center (seconds remaining); 0 = inactive. */
  recenterSteerRemain: number;
  /** Who the boss wants to hit — cumulative threat table (ally unit ids). */
  aggro: AggroTable;
  /** Sticky chase / leap victim id (-1 = none). */
  aggroTargetId: number;
};

export type RaidUnit = {
  id: number;
  defId: string;
  classId: RaidClassId;
  team: 0 | 1;
  x: number;
  y: number;
  vx: number;
  vy: number;
  intentVx: number;
  intentVy: number;
  hp: number;
  maxHp: number;
  damage: number;
  radius: number;
  speed: number;
  attackRange: number;
  attackCd: number;
  attackCdLeft: number;
  /** Class active skill id (`shield_bash` / `marked_shot` / `heal`); null for adds. */
  skillId: string | null;
  /** Active skill cooldown remaining (seconds). */
  skillCdLeft: number;
  /** Active skill cooldown max (seconds); 0 = no active. */
  skillCdMax: number;
  /** Presentation swing timer (seconds remaining). */
  attackAnimT: number;
  /** Soft crowd stun (e.g. boss leap land). */
  stunnedUntil: number;
  /** Timed knock flight (ground ease + display apex). */
  knock: RaidKnockFlight | null;
  /** Presentation / combat aim point (attack, heal, or flee threat). */
  aimX: number;
  aimY: number;
  /** Decision memory (engine behavior state + sticky target). */
  brain: RaidUnitBrain;
  /** Combat memory: who hit me, how hard lately (engine hit-signal). */
  hit: HitSignal;
  /** Seconds until next big-hit bark allowed (allies). */
  barkCdLeft: number;
  /** Hostile threat table (add ids; boss = RAID_AGGRO_BOSS_ID). */
  aggro: AggroTable;
  /** Elite brute cast telegraph (null for thralls/allies). */
  telegraph: RaidTelegraph | null;
  /** Elite brute leap (null for thralls/allies). */
  leap: RaidLeap | null;
  /** Brute brass cooldown remain; 0 for thralls. */
  brassCd: number;
  /** Brute leap cooldown remain; 0 for thralls / chargers use this for charge. */
  leapCd: number;
  /** Ironhorn brute charge cooldown remain; 0 for non-ironhorn / thralls. */
  chargeCd: number;
  /** Brute toughness remain; 0 for thralls. */
  toughness: number;
  toughnessMax: number;
  /** Duration of current brute break stun (HUD remain-frac); 0 when not broken. */
  breakStunDuration: number;
  /**
   * Elite brute combat gate — false during entrance drop.
   * Thralls/allies are always true.
   */
  combatArmed: boolean;
  /** Entrance drop countdown (seconds); 0 when armed / non-brute. */
  dropRemain: number;
};

export type RaidKnockFlight = {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  apex: number;
  dur: number;
  /** Elapsed seconds (0..dur). */
  t: number;
  /** Hitstun seconds applied when flight ends (0 = none). */
  recoverSec: number;
};

export type RaidCommander = {
  rallyCd: number;
  focusCd: number;
  callAddCd: number;
  regroupCd: number;
  retreatCd: number;
  holdCd: number;
  rallyUntil: number;
  focusUntil: number;
  damageMul: number;
  /** Active short directive (single slot, latest wins, auto-expires). */
  order: OrderSlot<RaidOrderKind>;
  /** Commander mana (gesture orders spend; regenerates over time). */
  mana: number;
  manaMax: number;
  /** Last gesture/order messageKey for HUD feedback. */
  lastActionKey: string | null;
  /** Sim time until which lastActionKey may echo on the prep chip. */
  lastActionUntil: number;
};

/** Continuous encounter: assault waves → transition → boss room. */
export type RaidEncounterMode = "assault" | "transition" | "boss";

export type RaidTransitionPhase = "retreat" | "drop" | "hold";

export type RaidFormationGoal = {
  unitId: number;
  x: number;
  y: number;
};

export type RaidEncounterState = {
  mode: RaidEncounterMode;
  scriptId: string;
  /** Current wave index (0-based) during assault. */
  waveIndex: number;
  /** True once the current wave drip has started (may still have toSpawn>0). */
  waveSpawned: boolean;
  /** Hostiles remaining to drip-spawn for the current wave. */
  toSpawn: number;
  /**
   * Of `toSpawn`, how many must still come from the south wall.
   * North remainder = toSpawn − toSpawnSouth. Wave `southFrac` sets the quota.
   */
  toSpawnSouth: number;
  /** Seconds until the next drip chunk. */
  nextChunkIn: number;
  /** Whether the brute pack for the current wave has been spawned. */
  bruteSpawnedForWave: boolean;
  /**
   * Bit i set ⇒ mid-drip charger row i (sorted by atDripFrac) has fired this wave.
   * Lets W3 fire 2+2 at different drip fracs without a one-shot boolean.
   */
  chargerMidDripFiredMask: number;
  /**
   * True when all mid-drip rows for the wave have fired and the with-brute
   * charger slot is resolved (count may be 0).
   */
  chargerSpawnedForWave: boolean;
  /** Countdown before next wave spawn. */
  delayRemain: number;
  /** Transition phase countdown (retreat / drop / hold). */
  transitionRemain: number;
  /** Active transition sub-phase; null outside transition. */
  transitionPhase: RaidTransitionPhase | null;
  /** Boss combat + HUD armed only after drop/hold completes. */
  bossCombatArmed: boolean;
  /** Ally retreat goals during transition retreat (cleared after). */
  formationGoals: RaidFormationGoal[] | null;
  /**
   * Shared thrall bark cooldown (assault drip chunk + mid-wave combat).
   * Prevents add spam when many thralls are alive.
   */
  addBarkCdLeft: number;
  /**
   * Stage escalate clock start (`state.time`). `-1` = inactive (delay /
   * transition / between stages). Reset on every wave drip start and boss arm —
   * never carries stacks across stages.
   */
  escalateAnchorTime: number;
};

/** Short-term enemy spawn heat (adjutant prediction). See raid-spawn-heat.ts. */
export type RaidSpawnHeatState = {
  samples: { x: number; y: number; t: number }[];
};

export type RaidBattleState = {
  time: number;
  phase: RaidPhase;
  /** Seconds left in bossDying / losing hold before won / lost. */
  resultHoldRemain: number;
  nextId: number;
  units: RaidUnit[];
  boss: RaidBoss;
  commander: RaidCommander;
  encounter: RaidEncounterState;
  events: RaidSimEvent[];
  partyIds: string[];
  /** Keyed ticker situations; HUD resolves display via priority + clear rules. */
  situations: Partial<Record<RaidSituationId, RaidSituationSlot>>;
  /** Experiential spawn heat for adjutant screen prediction. */
  spawnHeat: RaidSpawnHeatState;
  /**
   * Optional injected [0,1) sim RNG (Sim-First Phase 2).
   * Not serialized — hydrate restores via `opts.rng` / `withRaidRng`.
   * Assault drip / spawn / CD jitter only — not bark (see cosmeticRng).
   */
  rng?: () => number;
  /**
   * Optional injected [0,1) cosmetic bark RNG (E1 stream split).
   * Not serialized. Bark must never draw from `rng`.
   */
  cosmeticRng?: () => number;
};
